import React, { useRef, useEffect, useState } from 'react';
import { useProject } from '../../hooks/useProject';
import { usePreProcessingMetrics } from '../../hooks/usePreProcessingMetrics';
import { hasPermission } from '../../types/project';
import { formatBytes } from '../../utilities/format';
import { ProcessingReportModal } from './ProcessingReportModal';
import { PieChartPanel } from '../../components/charts/PieChartPanel';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { DownloadButton } from '../../components/DownloadButton';

type DataSelection = 'Extension' | 'Category';

function LiquidGauge({ value, total }: { value: number; total: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const id = useRef(`gauge-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || value <= 0 || total <= 0) return;
    container.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('id', id);
    svg.style.cssText = 'width:250px;height:323px;display:initial';
    container.appendChild(svg);
    const config = {
      minValue: 0,
      maxValue: total + total * 0.2,
      circleThickness: 0.05,
      circleFillGap: 0.05,
      circleColor: '#178BCA',
      waveHeight: 0.03,
      waveCount: 1,
      waveRiseTime: 1000,
      waveAnimateTime: 1000,
      waveRise: true,
      waveHeightScaling: true,
      waveAnimate: true,
      waveColor: '#178BCA',
      waveOffset: 0,
      textVertPosition: 0.5,
      textSize: 0.7,
      valueCountUp: false,
      displayPercent: false,
      textColor: '#045681',
      waveTextColor: '#A4DBf8',
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new (window as any).liquidFillGauge().loadLiquidFillGauge(
      { attr: (name: string) => name === 'id' ? id : '' },
      value,
      formatBytes(value),
      config,
    );

    // The wave animation recurses forever via transition.on('end'); interrupt it
    // (and remove the SVG) on unmount/update or it keeps ticking on detached DOM.
    return () => {
      const d3 = (window as any).d3;
      if (d3) {
        const sel = d3.select('#' + id);
        sel.interrupt();
        sel.selectAll('*').interrupt();
      }
      container.innerHTML = '';
    };
  }, [value, total, id]);

  return <div ref={containerRef} style={{ display: 'inline-block', width: 250, height: 323 }} />;
}

interface Props {
  projectId: string;
}

export function PreProcessingTab({ projectId }: Props) {
  const { data: project, isLoading: isLoadingProject } = useProject(projectId);
  const canManageReports = project ? hasPermission(project, 'MANAGE_REPORTS') : false;
  const { data: metrics, isLoading: isLoadingMetrics } = usePreProcessingMetrics(projectId);
  const [selection, setSelection] = useState<DataSelection>('Extension');
  const [showReport, setShowReport] = useState(false);

  const collectionCount = project?.numCollections ?? 0;
  const custodianCount = project?.custodians?.length ?? 0;
  const sourceSize = metrics?.projectSourceSize ?? 0;
  const outputSize = (metrics?.projectSourceSize ?? 0) + (metrics?.projectOutputSize ?? 0);
  const gaugeTotal = Math.max(sourceSize, outputSize);

  const sizeData = selection === 'Extension' ? (metrics?.extensionBySize ?? []) : (metrics?.typeBySize ?? []);
  const countData = selection === 'Extension' ? (metrics?.extensionByCount ?? []) : (metrics?.typeByCount ?? []);
  const sizeTitle = selection === 'Extension' ? 'Extension by Size' : 'Category by Size';
  const countTitle = selection === 'Extension' ? 'Extension by Count' : 'Category by Count';

  if (isLoadingProject || isLoadingMetrics) return <LoadingPlaceholder />;

  return (
    <>
      <div className="clearfix">
        <h2 className="pull-left">
          <i className="far fa-lightbulb" aria-hidden="true" />{' '}
          {project?.name} Overview
        </h2>
        {canManageReports && (
          <DownloadButton onClick={() => setShowReport(true)} />
        )}
      </div>

      <p className="lead">
        This Project contains {collectionCount} Collections and {custodianCount} Custodians
      </p>

      <div className="row">
        <div className="col-md-5 text-center">
          <h2>Pre-Processing</h2>
          <LiquidGauge value={sourceSize} total={gaugeTotal} />
        </div>
        <div className="col-md-2 text-center hidden-xs" style={{ marginTop: 150, marginLeft: -25 }}>
          <i className="fas fa-arrow-right" style={{ fontSize: 128, opacity: 0.07 }} aria-hidden="true" />
        </div>
        <div className="col-md-2 visible-xs" style={{ marginLeft: 110, marginTop: 20 }}>
          <i className="fas fa-arrow-down" style={{ fontSize: 128, opacity: 0.07 }} aria-hidden="true" />
        </div>
        <div className="col-md-5 text-center">
          <h2>Post-Processing</h2>
          <LiquidGauge value={outputSize} total={gaugeTotal} />
        </div>
      </div>

      <div style={{ marginTop: 150 }}>
        <div className="row">
          <h2>Data Overview</h2>
          <div className="btn-group">
            {(['Extension', 'Category'] as DataSelection[]).map(s => (
              <label
                key={s}
                className={`btn btn-default${selection === s ? ' active' : ''}`}
                onClick={() => setSelection(s)}
              >
                {s}
              </label>
            ))}
          </div>

          {metrics && (
            <div style={{ marginTop: 44 }}>
              <PieChartPanel key={`size-${selection}`} title={sizeTitle} data={sizeData} valueType="bytes" />
              <div className="col-md-2" />
              <PieChartPanel key={`count-${selection}`} title={countTitle} data={countData} valueType="count" />
            </div>
          )}
        </div>
      </div>
      {showReport && (
        <ProcessingReportModal projectId={projectId} projectName={project?.name ?? ''} type="pre_processing_report"
          onClose={() => setShowReport(false)} />
      )}
    </>
  );
}
